import constants from "@/api/constants";
import type { ProviderData, ProviderDayData } from "@/classes/Self-Serve/SelfServeDataGenerator";
import type { DaySlot } from "@/types/scheduling";
import { getLocalStorage } from "@/utils/localStorage";
import type { SelfServeInfoData, SelfServeInputData } from "shared/types";
import { UsaStates } from "usa-states";

const baseKey: string = constants.getConst("localStorageBaseKey") || "thr";
const apiUrl = constants.getStageConst("apiUrl");
export const getMatchData = async (data: SelfServeInputData): Promise<SelfServeInfoData> => {
  const res = await fetch(apiUrl + "match/match", {
    method: "POST",
    body: JSON.stringify(data),
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
  });
  // console.debug("match/match input", data);
  const json = await res.json();
  // console.debug("match/match output", json);
  return json.result;
};

const loadFromLocalStorage = (): SelfServeInfoData | null => {
  const data = localStorage.getItem("self_serve_data");
  const parsed: SelfServeInfoData = JSON.parse(data || "{}");
  if (!parsed?.user || !parsed?.rds) return null;
  if (
    parsed?.appointmentData &&
    !parsed.rds.map((r) => r.id).includes(<number>(<unknown>parsed?.appointmentData?.provider_id))
  ) {
    return null;
  }
  return parsed;
};

const saveToLocalStorage = (data: SelfServeInfoData) => {
  const selfServeData = localStorage.getItem("self_serve_data");
  const parsedData = selfServeData ? JSON.parse(selfServeData) : {};
  const newData = { ...parsedData, ...data };
  localStorage.setItem("self_serve_data", JSON.stringify(newData));
};

const transformState = (state: string) => {
  const states = new UsaStates().states;
  if (!state) return "";
  return states.find((s) => s.name.toLowerCase() === state.toLowerCase())?.abbreviation || "";
};

const getQuizData = () => {
  const data = getLocalStorage("quizInstance");
  const quizAnswers = data ? JSON.parse(data).answers || {} : {};
  if (!quizAnswers) throw new Error("No quiz answers found");
  const obj = {
    preference_gender: quizAnswers?.preference_gender,
    preference_age: quizAnswers?.preference_age,
    name: quizAnswers?.name,
    height: quizAnswers?.height,
    weight: quizAnswers?.weight,
    goal_weight: quizAnswers?.goal_weight,
    goal_health: quizAnswers?.goal_health,
    goal_body_composition: quizAnswers?.goal_body_composition,
    health_risk: quizAnswers?.health_risk,
    age: quizAnswers?.age,
    sex: quizAnswers?.sex,
    state: transformState(quizAnswers?.state),
    health_diagnosis: quizAnswers?.health_diagnosis,
    food_relationship: quizAnswers?.food_relationship,
    pregnant: quizAnswers?.pregnant,
    menopause: quizAnswers?.menopause,
    eating_disorder_diagnosis: quizAnswers?.eating_disorder_diagnosis,
    nutrition_area: quizAnswers?.nutrition_area,
  };
  return obj;
};

export const filterAndMapProviderData = (
  providerData: ProviderData[] | undefined,
  daySlots: DaySlot[],
  selectedDay: string,
): ProviderDayData[] | undefined => {
  if (!providerData || providerData?.length === 0) return undefined;

  const rdesForDay = daySlots.find((day) => day.day === selectedDay)?.rdes.map((rde) => rde.rde.toString()) || [];

  return providerData
    .filter((rd) => rdesForDay.includes(rd.id.toString()))
    .map((filteredRDE) => {
      const { days, dayCount, ...rest } = filteredRDE;
      const filteredDay = days.find((day) => day.day === selectedDay);
      if (!filteredDay) return null;
      return {
        ...rest,
        hours: filteredDay.hours,
        slotCount: filteredDay.slotCount,
      };
    })
    .filter((item): item is ProviderDayData => item !== null)
    .sort((a, b) => b.slotCount - a.slotCount); // Sort in descending order by slotCount
};
